import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Edit } from 'react-feather';
import { GetOrderQuery as GetOrder, Permission } from '__generated__/graphql';
import { useSession } from '../../../Auth/SessionProvider';
import EditInvoiceForm from './EditInvoiceForm';
import InvoiceDetails, { hasInvoiceDetails } from '../../../../common/Invoice/InvoiceDetails';
import UI from '../../../../common/UI';
import useLocale from '../../../../common/useLocale';
import useProject from '../../../useProject';

type Order = GetOrder['order'];

export interface InvoiceSectionProps {
  order: Order;
}

const InvoiceSection = ({ order }: InvoiceSectionProps) => {
  const { t } = useTranslation();
  const { hasPermission } = useSession();
  const { formatDate, parseDate } = useLocale();
  const [showForm, setShowForm] = useState(false);
  const project = useProject();

  const hasEditPermission = hasPermission(project.id, [Permission.edit_registrations]);

  const handleSuccess = () => {
    setShowForm(false);
  };

  const handleCancel = () => {
    setShowForm(false);
  };

  const invoiceDetails = {
    company_name: order.company_name,
    reference: order.reference,
    street: order.street,
    house_number: order.house_number,
    zip_code: order.zip_code,
    extra_address_line: order.extra_address_line,
    city: order.city,
    country: order.country,
    phone: order.phone,
    vat_id: order.vat_id,
  };

  return (
    <UI.Div>
      {!showForm && (
        <UI.FadeIn>
          <UI.GridContainer sc={{ columns: ['1fr', '2fr 1fr'] }}>
            <UI.GridContainer>
              <UI.Div>
                <UI.Legend sc={{ fontWeight: 500 }}>
                  {t('contact')}
                </UI.Legend>
                {`${order.participant.first_name} ${order.participant.last_name}`}
              </UI.Div>
              {hasInvoiceDetails(invoiceDetails) && <InvoiceDetails {...invoiceDetails} />}
              <UI.Div>
                <UI.Legend sc={{ fontWeight: 500 }}>
                  {t('invoice_date')}
                </UI.Legend>
                {formatDate(
                  parseDate(order.invoice_date, { format: 'internal_date' }),
                  { format: 'display_date' },
                )}
              </UI.Div>
              <UI.Div>
                <UI.Legend sc={{ fontWeight: 500 }}>
                  {t('due_date')}
                </UI.Legend>
                {formatDate(
                  parseDate(order.due_date, { format: 'internal_date' }),
                  { format: 'display_date' },
                )}
              </UI.Div>
            </UI.GridContainer>

            {hasEditPermission && (
              <UI.Div sc={{ textAlign: ['left', 'right'] }}>
                <UI.Button
                  onClick={() => setShowForm(true)}
                  sc={{ blank: true, size: 'sm', my: -1 }}
                  aria-label={t('edit_invoice_details')}
                >
                  <UI.Icon>
                    <Edit />
                  </UI.Icon>
                  {' '}
                  {t('edit')}
                </UI.Button>
              </UI.Div>
            )}
          </UI.GridContainer>
        </UI.FadeIn>
      )}
      {showForm && (
        <UI.FadeIn>
          <UI.GridContainer sc={{ columns: ['1fr', '2fr 1fr'] }}>
            <EditInvoiceForm order={order} onSuccess={handleSuccess} onCancel={handleCancel} />
          </UI.GridContainer>
        </UI.FadeIn>
      )}
    </UI.Div>
  );
};

export default InvoiceSection;
